import type { HuacaClient } from '@huaca/ui';
import type { Instance, InstanceLink, InstanceSummary, Template, TemplateBody } from './types';

/** Claro product API — all calls go through the gated `/api/claro/...` surface. */

export async function listTemplates(client: HuacaClient): Promise<Template[]> {
  const r = await client.request<{ templates: Template[] }>('/api/claro/templates');
  return r.templates;
}

export async function getTemplate(client: HuacaClient, id: string): Promise<Template> {
  const r = await client.request<{ template: Template }>(`/api/claro/templates/${encodeURIComponent(id)}`);
  return r.template;
}

export async function createTemplate(client: HuacaClient, body: TemplateBody): Promise<Template> {
  const r = await client.request<{ template: Template }>('/api/claro/templates', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return r.template;
}

export async function updateTemplate(client: HuacaClient, id: string, body: TemplateBody): Promise<Template> {
  const r = await client.request<{ template: Template }>(`/api/claro/templates/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return r.template;
}

export async function archiveTemplate(client: HuacaClient, id: string): Promise<void> {
  await client.request(`/api/claro/templates/${encodeURIComponent(id)}/archive`, { method: 'POST' });
}

export async function listInstances(client: HuacaClient): Promise<InstanceSummary[]> {
  const r = await client.request<{ instances: InstanceSummary[] }>('/api/claro/instances');
  return r.instances;
}

export async function getInstance(client: HuacaClient, id: string): Promise<Instance> {
  const r = await client.request<{ instance: Instance }>(`/api/claro/instances/${encodeURIComponent(id)}`);
  return r.instance;
}

export async function createInstance(
  client: HuacaClient,
  body: { templateId: string; client: { fullName: string; phone: string; email: string } },
): Promise<Instance> {
  const r = await client.request<{ instance: Instance }>('/api/claro/instances', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return r.instance;
}

/**
 * Replace THIS onboarding's own snapshot form (Editor). The server recomputes completion and may
 * downgrade a completed onboarding back to in_progress; it returns the full updated instance.
 */
export async function updateInstanceSnapshot(client: HuacaClient, id: string, body: TemplateBody): Promise<Instance> {
  const r = await client.request<{ instance: Instance }>(`/api/claro/instances/${encodeURIComponent(id)}/snapshot`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return r.instance;
}

export async function setInstanceLink(
  client: HuacaClient,
  id: string,
  action: 'revoke' | 'renew',
): Promise<InstanceLink> {
  const r = await client.request<{ link: InstanceLink }>(`/api/claro/instances/${encodeURIComponent(id)}/link`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
  return r.link;
}

/**
 * A short-lived signed URL for the pro to download a client's uploaded file. The URL is generated
 * fresh each time and never persisted — so it is fetched on demand (at click), not stored on the row.
 */
export async function getFileDownloadUrl(client: HuacaClient, instanceId: string, fieldId: string): Promise<string> {
  const r = await client.request<{ url: string }>(
    `/api/claro/instances/${encodeURIComponent(instanceId)}/files/${encodeURIComponent(fieldId)}/download-url`,
  );
  return r.url;
}
